import { useRef, useState } from 'react';
import { UploadCloud } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
  Button,
  Input,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { EmptyState } from '@vnetwork/ui/data';
import { useToast } from '@vnetwork/ui/feedback';
import {
  useStorages,
  useUploadUrl,
  useCreateAsset,
  uploadFileToStorage,
} from '@/api/fastchannel';
import { trackFeatureAction } from '@/lib/analytics-events';
import { formatBytes } from '@/pages/services/_shared/analytics-format';
import styles from './UploadAssetDialog.module.css';

interface UploadAssetDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  serviceUuid: string;
}

/** Sanitize a filename into a safe object key (no leading slash, spaces→`_`). */
function toObjectKey(prefix: string, filename: string): string {
  const safeName = filename.trim().replace(/\s+/g, '_').replace(/^\/+/, '');
  const cleanPrefix = prefix.trim().replace(/^\/+/, '').replace(/\/+$/, '');
  return cleanPrefix ? `${cleanPrefix}/${safeName}` : safeName;
}

/**
 * Upload a local file into a storage and register it as a Content Library asset.
 * Three-step flow: presign (control plane) → direct PUT (storage) → POST /assets
 * (control plane). Each step's failure is surfaced via toast; nothing is silent.
 */
export function UploadAssetDialog({ open, onOpenChange, serviceUuid }: UploadAssetDialogProps) {
  const { show } = useToast();
  const { data: storages = [], isLoading: storagesLoading } = useStorages(serviceUuid);

  const [storageId, setStorageId] = useState('');
  const [prefix, setPrefix] = useState('');
  const [name, setName] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  /** Upload byte progress; null while not in the storage-PUT phase. */
  const [uploaded, setUploaded] = useState<{ loaded: number; total: number } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const presign = useUploadUrl();
  const createAsset = useCreateAsset(serviceUuid);

  const activeStorage = storages.find((s) => s.id === storageId) ?? storages[0];
  const valid = !!activeStorage && !!file && !busy;

  const onPickFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = e.target.files?.[0] ?? null;
    setFile(picked);
    // Default the asset name to the filename (without extension) on first pick.
    if (picked && !name) setName(picked.name.replace(/\.[^.]+$/, ''));
  };

  const reset = () => {
    setFile(null);
    setPrefix('');
    setName('');
    setUploaded(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const submit = async () => {
    if (!activeStorage || !file) return;
    setBusy(true);
    setUploaded({ loaded: 0, total: file.size });
    const objectKey = toObjectKey(prefix, file.name);
    const contentType = file.type || 'application/octet-stream';
    try {
      // 1. Presign (control plane).
      const descriptor = await presign.mutateAsync({
        storageId: activeStorage.id,
        object_key: objectKey,
        content_type: contentType,
      });
      // 2. Direct browser → storage PUT (the only non-control-plane call).
      await uploadFileToStorage(descriptor, file, (loaded, total) =>
        setUploaded({ loaded, total }),
      );
      // 3. Register the asset (control plane).
      await createAsset.mutateAsync({
        storage_id: activeStorage.id,
        object_key: objectKey,
        name: name.trim() || undefined,
        size_bytes: file.size,
        content_type: contentType,
      });
      show({ tone: 'success', title: 'Asset uploaded' });
      trackFeatureAction('fastchannel', 'asset', 'create');
      reset();
      onOpenChange(false);
    } catch (err) {
      const detail = err instanceof Error ? err.message : 'Unknown error';
      show({ tone: 'danger', title: 'Upload failed', description: detail });
    } finally {
      setBusy(false);
      setUploaded(null);
    }
  };

  // Bytes done flushing but server still responding → indeterminate tail.
  const pct = uploaded && uploaded.total > 0
    ? Math.round((uploaded.loaded / uploaded.total) * 100)
    : 0;
  const flushing = busy && pct >= 100;

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Upload asset</DialogTitle>
          <DialogDescription>
            Upload a media file to a storage and add it to the content library.
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          {storagesLoading ? (
            <EmptyState icon={<UploadCloud size={20} />} title="Loading storages…" />
          ) : storages.length === 0 ? (
            <EmptyState
              icon={<UploadCloud size={20} />}
              title="No storage attached"
              description="Add an S3/MinIO storage first, then upload media to it."
            />
          ) : (
            <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
              <Field label="Storage" required>
                {() => (
                  <Select value={activeStorage?.id ?? ''} onValueChange={setStorageId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Choose storage" />
                    </SelectTrigger>
                    <SelectContent>
                      {storages.map((s) => (
                        <SelectItem key={s.id} value={s.id}>
                          {s.title || s.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </Field>

              <Field label="File" required>
                {() => (
                  <div style={{ display: 'flex', gap: 'var(--vn-space-2)', alignItems: 'center' }}>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="video/*,.mp4,.m3u8,.ts"
                      onChange={onPickFile}
                      disabled={busy}
                      style={{ flex: 1, minWidth: 0, fontSize: 'var(--vn-font-size-sm)' }}
                    />
                  </div>
                )}
              </Field>
              {file && !uploaded && (
                <span style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>
                  {file.name} · {formatBytes(file.size)}
                </span>
              )}

              {uploaded && (
                <div
                  className={styles.progress}
                  role="progressbar"
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={flushing ? undefined : pct}
                  aria-label="Upload progress"
                >
                  <div className={styles.meta}>
                    <span>
                      {flushing
                        ? 'Finalizing…'
                        : `${formatBytes(uploaded.loaded)} / ${formatBytes(uploaded.total)}`}
                    </span>
                    {!flushing && <span className={styles.percent}>{pct}%</span>}
                  </div>
                  <div className={styles.track}>
                    <div
                      className={styles.bar}
                      data-indeterminate={flushing || undefined}
                      style={flushing ? undefined : { width: `${pct}%` }}
                    />
                  </div>
                </div>
              )}

              <Field label="Folder" description="Optional key prefix inside the storage.">
                {(props) => (
                  <Input
                    {...props}
                    value={prefix}
                    placeholder="vod/2026"
                    onChange={(e) => setPrefix(e.target.value)}
                    disabled={busy}
                  />
                )}
              </Field>

              <Field label="Name" description="Display name in the library. Defaults to the filename.">
                {(props) => (
                  <Input
                    {...props}
                    value={name}
                    placeholder="Program name"
                    onChange={(e) => setName(e.target.value)}
                    disabled={busy}
                  />
                )}
              </Field>
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" disabled={busy} onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={!valid}
            startIcon={<UploadCloud size={15} />}
            onClick={() => void submit()}
          >
            {busy ? 'Uploading…' : 'Upload'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
